import React, { ChangeEvent, useState } from 'react';
import { Button, ButtonState } from '@highlight-ui/button';
import { ColorPicker } from '@highlight-ui/color-picker';
import { TextInput } from '@highlight-ui/input';
import { noop } from '@highlight-ui/utils-commons';

import { AddOptionsFooterProps } from '../types';
import styles from './AddOptionsFooter.module.scss';

const deriveFooterButtonState = (
  inputValue: string,
  loading?: boolean,
): ButtonState => {
  if (inputValue.length === 0) return 'disabled';
  if (loading) return 'loading';
  return 'default';
};

type PassedProps = {
  handleVisibility?: typeof noop;
  closeOnSelect?: boolean;
  onKeyDown?: (
    e: React.KeyboardEvent<HTMLElement>,
    isLastElement?: boolean,
  ) => void;
};

function AddOptionsFooter({
  createButtonProps,
  inputProps,
  onCreate,
  colorPickerOptions,
  multiple = false,
  ...passedProps
}: AddOptionsFooterProps) {
  const {
    handleVisibility = noop,
    closeOnSelect,
    onKeyDown = noop,
  } = passedProps as PassedProps;

  const [inputValue, setInputValue] = useState('');
  const [color, setColor] = useState<string | undefined>(
    colorPickerOptions?.initialColor,
  );

  const onAddHandler = () => {
    onCreate({
      value: inputValue || '',
      ...(color ? { color } : {}),
    });
    setInputValue('');
    if (closeOnSelect && !multiple) handleVisibility();
  };

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const buttonState = deriveFooterButtonState(
    inputValue,
    createButtonProps.loading,
  );

  return (
    <footer className={styles.creatableFooterContainer}>
      <TextInput
        placeholder={inputProps?.placeholder}
        className={styles.inputWrapper}
        onKeyPress={(e) => {
          if (e.key.toLowerCase() === 'enter' && inputValue) onAddHandler();
        }}
        metadata={inputProps?.metadata}
        onChange={onInputChange}
        value={inputValue}
        onKeyDown={(e) =>
          onKeyDown(e, buttonState !== 'default' && !colorPickerOptions)
        }
      />

      {colorPickerOptions && (
        <ColorPicker
          closeOnSelect
          initialColor={color}
          metadata={colorPickerOptions.metadata}
          onChange={setColor}
          palette={colorPickerOptions.palette}
          title={colorPickerOptions.title}
        />
      )}

      <Button
        variant="emphasized"
        onClick={onAddHandler}
        buttonState={buttonState}
        onKeyDown={(e) => onKeyDown(e, true)}
      >
        {createButtonProps.label}
      </Button>
    </footer>
  );
}

export default AddOptionsFooter;
